import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { PhoneNumberInput } from '@/components/PhoneNumberInput.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { links } from '@/lib/constants.ts';
import { emailSchema, phoneNumberSchema } from '@/lib/validation-schemas.ts';

const formId = 'insurance-send-link-form';
const formSchema = z.object({
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
  consent: z.boolean().refine((value) => value, {
    message: 'Please agree before continuing',
  }),
});

export type InsuranceSendLinkFormData = z.infer<typeof formSchema>;

interface Props {
  onSubmit: (data: InsuranceSendLinkFormData) => void;
}

export function InsuranceSendLinkForm({ onSubmit }: Props) {
  const form = useForm<InsuranceSendLinkFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phoneNumber: '',
      email: '',
      consent: false,
    },
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
      <div className="space-y-6 sm:space-y-8">
        <p className="text-main text-base leading-6">
          Still can&apos;t find it? We&apos;ll text and email you a reminder to pick up where you
          left off.
        </p>

        <FieldGroup>
          <div className="space-y-7">
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-phoneNumber`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Phone number</FieldLabel>
                    <PhoneNumberInput {...field} id={inputId} aria-invalid={fieldState.invalid} />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />

            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => {
                const inputId = `${formId}-email`;

                return (
                  <Field data-invalid={fieldState.invalid}>
                    <FieldLabel htmlFor={inputId}>Email</FieldLabel>
                    <Input
                      {...field}
                      id={inputId}
                      type="email"
                      aria-invalid={fieldState.invalid}
                      placeholder="example@email.com"
                      inputMode="email"
                    />
                    {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
                  </Field>
                );
              }}
            />
          </div>
        </FieldGroup>

        <Controller
          name="consent"
          control={form.control}
          render={({ field, fieldState }) => {
            const inputId = `${formId}-consent`;

            return (
              <Field data-invalid={fieldState.invalid} className="gap-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={inputId}
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    aria-invalid={fieldState.invalid}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={inputId}
                    className="text-charcoal-500 text-xs leading-snug font-normal"
                  >
                    By checking this box, you agree that Conduit Health may send you recurring
                    automated promotional text messages at the mobile number you provide about
                    Conduit Health&apos;s and its affiliates&apos; products and services. Consent is
                    not required to obtain care. Message &amp; data rates may apply. Message
                    frequency varies. Reply STOP to cancel and HELP for help. See our{' '}
                    <a href={links.conduit_privacy_policy_url} className="font-medium underline">
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href={links.conduit_terms_of_service_url} className="font-medium underline">
                      Terms of Service
                    </a>
                    .
                  </Label>
                </div>
                {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
              </Field>
            );
          }}
        />

        <Button type="submit" className="w-full shadow-sm">
          Send me a reminder
        </Button>
      </div>
    </form>
  );
}
